/**
 * GPX serialise / parse for routes.
 *
 * GPX is the format every plotter and nav app reads and writes (Navionics,
 * iSailor, OpenCPN, Garmin, savvy navvy), so it's how a route moves between this
 * app and the boat's kit. This module is the single, pure home for that: it turns
 * a Route into a GPX 1.1 string and reads a GPX string back into our route shape.
 * It imports only the Route / Waypoint TYPES, so it pulls in no store and no
 * Capacitor, and runs the same in the app webview and in a vitest (jsdom) test.
 *
 * Standard GPX carries the geometry (lat/lon, names, notes) that any software
 * understands. Our own per-route and per-leg extras (cross-track distance, arrival
 * radius, line colour, per-leg overrides) have no home in the base schema, so we
 * park them in a namespaced <extensions> block. Other software ignores extensions
 * it doesn't recognise, so a file we write opens cleanly elsewhere, and a file we
 * read back carries our data through untouched. Where our extension is absent, on
 * a plain third-party GPX, we fall back to the same defaults a hand-built route
 * would have (no colour, no XTD, no arrival alarm), so nothing breaks.
 *
 * Where our data lives:
 *   <rte><extensions><ardent:routeMeta color xtdNm arrivalRadiusNm createdAt>
 *        <ardent:leg index xtd note/> ... </ardent:routeMeta></extensions>
 *   <rtept><extensions><ardent:wptMeta arrivalRadiusNm arrivalAlarm icon/></extensions>
 * Per-leg overrides are keyed by leg INDEX (the leg out of waypoint[i]), not by
 * waypoint id, because ids are regenerated on import; the index is stable across a
 * round-trip.
 */
import type { Route, Waypoint } from './route';

/** Our extensions namespace. Prefixed `ardent:` on the elements below. */
const ARDENT_NS = 'https://ardent-training.com/xmlns/gpx/v1';
const CREATOR = 'Ardent Chartplotter';

/**
 * A track (<trk>) is a breadcrumb of where a boat has BEEN, not a plan of where
 * to go. A big logged track can hold thousands of points, so we never fold one
 * into a route (that would silently import 5000 "waypoints"). We ignore tracks
 * but count their points so the caller can say so.
 */
export interface GpxImportResult {
  /** Every route we could build: one per <rte>, or one assembled from loose <wpt>. */
  routes: Route[];
  /** Points dropped for a missing or non-numeric lat/lon (not WGS84-plottable). */
  skippedPoints: number;
  /** Track points seen and deliberately ignored (we don't import tracks as routes). */
  trackPointsIgnored: number;
}

// --- Serialise ---------------------------------------------------------------

/** Escape the five XML-significant characters so any text is safe in the output. */
function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/** A GPX coordinate, trimmed of any floating-point noise but kept full precision. */
function coord(n: number): string {
  return String(n);
}

/**
 * Serialise a route to a valid GPX 1.1 string: a single <rte> with one <rtept>
 * per waypoint, names on both the route and its points, and our own data in a
 * namespaced <extensions> block (see the module header for the layout).
 */
export function routeToGpx(route: Route): string {
  const now = new Date().toISOString();

  // Route-level extension: the whole-route settings, then a <leg> line for every
  // leg that carries an XTD or a note override. Leg data is keyed by the start
  // waypoint's id in our model, so look each leg up by its start id and emit it
  // under the stable leg index instead.
  const routeMetaAttrs: string[] = [];
  if (route.color) routeMetaAttrs.push(`color="${esc(route.color)}"`);
  if (typeof route.xtdNm === 'number') routeMetaAttrs.push(`xtdNm="${route.xtdNm}"`);
  if (typeof route.arrivalRadiusNm === 'number')
    routeMetaAttrs.push(`arrivalRadiusNm="${route.arrivalRadiusNm}"`);
  if (typeof route.createdAt === 'number') routeMetaAttrs.push(`createdAt="${route.createdAt}"`);

  const legLines: string[] = [];
  for (let i = 0; i < route.waypoints.length - 1; i++) {
    const startId = route.waypoints[i].id;
    const xtd = route.legXtd?.[startId];
    const note = route.legNotes?.[startId];
    if (xtd === undefined && note === undefined) continue;
    const parts = [`index="${i}"`];
    if (typeof xtd === 'number') parts.push(`xtd="${xtd}"`);
    if (note !== undefined) parts.push(`note="${esc(note)}"`);
    legLines.push(`        <ardent:leg ${parts.join(' ')}/>`);
  }

  const hasRouteMeta = routeMetaAttrs.length > 0 || legLines.length > 0;
  let routeExt = '';
  if (hasRouteMeta) {
    const open = `<ardent:routeMeta${routeMetaAttrs.length ? ' ' + routeMetaAttrs.join(' ') : ''}`;
    routeExt = legLines.length
      ? `\n    <extensions>\n      ${open}>\n${legLines.join('\n')}\n      </ardent:routeMeta>\n    </extensions>`
      : `\n    <extensions>\n      ${open}/>\n    </extensions>`;
  }

  const rtepts = route.waypoints.map((w) => rteptXml(w)).join('\n');

  const meta =
    `  <metadata>\n` +
    `    <name>${esc(route.name)}</name>\n` +
    (route.notes ? `    <desc>${esc(route.notes)}</desc>\n` : '') +
    `    <link href="https://ardent-training.com"><text>Ardent Chartplotter</text></link>\n` +
    `    <time>${now}</time>\n` +
    `  </metadata>\n`;

  const rteDesc = route.notes ? `\n    <desc>${esc(route.notes)}</desc>` : '';

  return (
    `<?xml version="1.0" encoding="UTF-8"?>\n` +
    `<gpx version="1.1" creator="${CREATOR}" ` +
    `xmlns="http://www.topografix.com/GPX/1/1" ` +
    `xmlns:ardent="${ARDENT_NS}">\n` +
    meta +
    `  <rte>\n` +
    `    <name>${esc(route.name)}</name>${rteDesc}${routeExt}\n` +
    rtepts +
    `\n  </rte>\n` +
    `</gpx>\n`
  );
}

/** One <rtept>, with standard name/desc/sym and our per-waypoint extension. */
function rteptXml(w: Waypoint): string {
  const lines: string[] = [];
  if (w.name) lines.push(`      <name>${esc(w.name)}</name>`);
  if (w.description) lines.push(`      <desc>${esc(w.description)}</desc>`);
  // <sym> is the standard "symbol" field; write the icon there too so other
  // software shows something sensible, and keep the exact id in our extension.
  if (w.icon) lines.push(`      <sym>${esc(w.icon)}</sym>`);

  const wptAttrs: string[] = [];
  if (typeof w.arrivalRadiusNm === 'number')
    wptAttrs.push(`arrivalRadiusNm="${w.arrivalRadiusNm}"`);
  if (typeof w.arrivalAlarm === 'boolean') wptAttrs.push(`arrivalAlarm="${w.arrivalAlarm}"`);
  if (w.icon) wptAttrs.push(`icon="${esc(w.icon)}"`);
  if (wptAttrs.length) {
    lines.push(`      <extensions><ardent:wptMeta ${wptAttrs.join(' ')}/></extensions>`);
  }

  const open = `    <rtept lat="${coord(w.position[1])}" lon="${coord(w.position[0])}">`;
  if (lines.length === 0) {
    return `    <rtept lat="${coord(w.position[1])}" lon="${coord(w.position[0])}"/>`;
  }
  return `${open}\n${lines.join('\n')}\n    </rtept>`;
}

// --- Parse -------------------------------------------------------------------

/**
 * Parse a GPX string into route(s). Import scope:
 *   - Every <rte> becomes a route (a file with several routes imports them all).
 *   - A file with only loose <wpt> and no <rte> is assembled into ONE route, so a
 *     plain list of marks becomes a usable passage. If the file has routes, loose
 *     <wpt> are left alone (they're usually the route's own points repeated).
 *   - <trk> tracks are ignored (see GpxImportResult); their point count is
 *     reported so the caller can mention it.
 * Our <extensions> are read back when present; a third-party GPX without them
 * falls back to plain defaults. Points with a missing or non-numeric lat/lon are
 * skipped and counted. Throws a clear Error only when the text isn't parseable
 * GPX at all.
 */
export function gpxToRoutes(gpxString: string): GpxImportResult {
  if (typeof gpxString !== 'string' || gpxString.trim() === '') {
    throw new Error('The file is empty, so there is nothing to import.');
  }

  const doc = new DOMParser().parseFromString(gpxString, 'application/xml');
  // A parse failure shows up as a <parsererror> element rather than a thrown
  // error, so check for it and turn it into a clear message.
  if (doc.getElementsByTagName('parsererror').length > 0) {
    throw new Error("This file isn't valid XML, so it can't be read as GPX.");
  }
  const root = doc.documentElement;
  if (!root || root.localName !== 'gpx') {
    throw new Error("This doesn't look like a GPX file (no <gpx> root element).");
  }

  let skippedPoints = 0;
  const routes: Route[] = [];
  let routeSeq = 0;
  let wpSeq = 0;
  const nextRouteId = () => `imp-r-${++routeSeq}`;
  const nextWpId = () => `imp-wp-${++wpSeq}`;

  // Read every <rte> into a route.
  const rteEls = descendantsByLocal(root, 'rte');
  for (const rte of rteEls) {
    const name = directText(rte, 'name') ?? `Imported route ${routeSeq + 1}`;
    const notes = directText(rte, 'desc');
    const waypoints: Waypoint[] = [];
    for (const rtept of descendantsByLocal(rte, 'rtept')) {
      const wp = readPoint(rtept, nextWpId);
      if (wp) waypoints.push(wp);
      else skippedPoints++;
    }
    if (waypoints.length === 0) continue;
    const route: Route = { id: nextRouteId(), name, waypoints };
    if (notes) route.notes = notes;
    applyRouteMeta(rte, route);
    routes.push(route);
  }

  // Loose <wpt>: assemble into one route only when the file carries no routes.
  const wptEls = descendantsByLocal(root, 'wpt');
  if (routes.length === 0 && wptEls.length > 0) {
    const waypoints: Waypoint[] = [];
    for (const wpt of wptEls) {
      const wp = readPoint(wpt, nextWpId);
      if (wp) waypoints.push(wp);
      else skippedPoints++;
    }
    if (waypoints.length > 0) {
      const name = directText(root, 'name') ?? metadataName(root) ?? 'Imported waypoints';
      routes.push({ id: nextRouteId(), name, waypoints });
    }
  }

  // Count track points so a track-only file reports what it left behind rather
  // than looking like it imported nothing for no reason.
  const trackPointsIgnored = descendantsByLocal(root, 'trkpt').length;

  return { routes, skippedPoints, trackPointsIgnored };
}

/** Read a <rtept> or <wpt> into a Waypoint, or null if its lat/lon is unusable. */
function readPoint(el: Element, nextWpId: () => string): Waypoint | null {
  const lat = parseFloat(el.getAttribute('lat') ?? '');
  const lon = parseFloat(el.getAttribute('lon') ?? '');
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  // Guard against out-of-range coordinates (a non-WGS84 or corrupt file), which
  // would plot the point off the earth. Treat those as unusable too.
  if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return null;

  const wp: Waypoint = { id: nextWpId(), position: [lon, lat] };
  const name = directText(el, 'name');
  if (name) wp.name = name;
  const desc = directText(el, 'desc') ?? directText(el, 'cmt');
  if (desc) wp.description = desc;

  // Our per-waypoint extension wins; fall back to the standard <sym> for the icon.
  const meta = firstDescendantByLocal(el, 'wptMeta');
  const sym = directText(el, 'sym');
  if (meta) {
    const r = parseFloat(meta.getAttribute('arrivalRadiusNm') ?? '');
    if (Number.isFinite(r)) wp.arrivalRadiusNm = r;
    const alarm = meta.getAttribute('arrivalAlarm');
    if (alarm === 'true') wp.arrivalAlarm = true;
    else if (alarm === 'false') wp.arrivalAlarm = false;
    const icon = meta.getAttribute('icon') ?? sym;
    if (icon) wp.icon = icon;
  } else if (sym) {
    wp.icon = sym;
  }
  return wp;
}

/** Read <ardent:routeMeta> (and its <ardent:leg> lines) back onto a route. */
function applyRouteMeta(rte: Element, route: Route): void {
  const meta = firstDescendantByLocal(rte, 'routeMeta');
  if (!meta) return;
  const color = meta.getAttribute('color');
  if (color) route.color = color;
  const xtd = parseFloat(meta.getAttribute('xtdNm') ?? '');
  if (Number.isFinite(xtd)) route.xtdNm = xtd;
  const arr = parseFloat(meta.getAttribute('arrivalRadiusNm') ?? '');
  if (Number.isFinite(arr)) route.arrivalRadiusNm = arr;
  const created = parseFloat(meta.getAttribute('createdAt') ?? '');
  if (Number.isFinite(created)) route.createdAt = created;

  // Per-leg overrides come keyed by leg index; re-key them onto the start
  // waypoint id of that leg in the freshly-parsed route.
  const legXtd: Record<string, number> = {};
  const legNotes: Record<string, string> = {};
  for (const leg of descendantsByLocal(meta, 'leg')) {
    const idx = parseInt(leg.getAttribute('index') ?? '', 10);
    if (!Number.isInteger(idx) || idx < 0 || idx >= route.waypoints.length - 1) continue;
    const startId = route.waypoints[idx].id;
    const x = parseFloat(leg.getAttribute('xtd') ?? '');
    if (Number.isFinite(x)) legXtd[startId] = x;
    const note = leg.getAttribute('note');
    if (note) legNotes[startId] = note;
  }
  if (Object.keys(legXtd).length) route.legXtd = legXtd;
  if (Object.keys(legNotes).length) route.legNotes = legNotes;
}

// --- Namespace-tolerant DOM helpers ------------------------------------------
// Real-world GPX comes with all sorts of namespace prefixes (gpx:rte, ns2:rtept)
// and mixed casing, so we match on localName (the tag without its prefix) rather
// than the full qualified name, which keeps us tolerant of whoever wrote the file.

/** Every descendant element with this localName, in document order. */
function descendantsByLocal(root: Element, local: string): Element[] {
  const out: Element[] = [];
  const all = root.getElementsByTagName('*');
  for (let i = 0; i < all.length; i++) {
    if (all[i].localName === local) out.push(all[i]);
  }
  return out;
}

/** First descendant element with this localName, or null. */
function firstDescendantByLocal(root: Element, local: string): Element | null {
  const all = root.getElementsByTagName('*');
  for (let i = 0; i < all.length; i++) {
    if (all[i].localName === local) return all[i];
  }
  return null;
}

/**
 * Text of the first DIRECT child with this localName, trimmed, or undefined.
 * Direct-child only so a <rte>'s own <name> isn't confused with an <rtept>'s
 * <name> nested inside it.
 */
function directText(el: Element, local: string): string | undefined {
  for (let i = 0; i < el.children.length; i++) {
    const c = el.children[i];
    if (c.localName === local) {
      const t = (c.textContent ?? '').trim();
      return t === '' ? undefined : t;
    }
  }
  return undefined;
}

/** The <metadata><name> of a GPX doc, for naming a loose-waypoint import. */
function metadataName(root: Element): string | undefined {
  const meta = firstDescendantByLocal(root, 'metadata');
  return meta ? directText(meta, 'name') : undefined;
}
